<template>
  <BaseModal
    :is-visible="isVisible"
    title="Incomplete Questions Detected"
    subtitle="Some questions don't meet the required criteria"
    :icon="AlertTriangle"
    size="2xl"
    type="warning"
    :show-footer="true"
  @close="closeModal"
  >
    <div class="space-y-6">
      <!-- Warning message -->
      <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div class="flex items-start">
          <AlertTriangle class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 class="font-medium text-amber-900 dark:text-amber-100 mb-2">
              Action Required
            </h4>
            <p class="text-sm text-amber-700 dark:text-amber-300">
              You have {{ incompleteCount }} incomplete question{{ incompleteCount !== 1 ? 's' : '' }} 
              that {{ incompleteCount !== 1 ? "don't" : "doesn't" }} meet the required criteria. 
              You can either review and update them or proceed to save anyway.
            </p>
          </div>
        </div>
      </div>

      <!-- List of incomplete questions -->
      <div class="space-y-4">
        <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
          Incomplete Questions:
        </h5>
        
        <div class="max-h-64 overflow-y-auto space-y-3">
          <div
            v-for="(question, index) in incompleteQuestions.filter(q => !q.isComplete)"
            :key="index"
            class="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/10"
          >
            <div class="flex items-start justify-between mb-2">
              <h6 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                Question {{ question.index + 1 }}
              </h6>
              <span class="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                Incomplete
              </span>
            </div>
            
            <!-- Question text preview -->
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
              {{ question.question || 'No question text entered' }}
            </p>
            
            <!-- Items to review list -->
            <div class="space-y-1">
              <p class="text-xs font-medium text-red-700 dark:text-red-300">Items to review:</p>
              <ul class="text-xs text-red-600 dark:text-red-400 space-y-1 pl-2">
                <li 
                  v-for="issue in question.issues" 
                  :key="issue" 
                  class="flex items-center gap-2"
                >
                  <span class="w-1 h-1 bg-red-500 rounded-full flex-shrink-0"></span>
                  {{ issue }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Action options -->
      <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          What would you like to do?
        </h5>
        <div class="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <div class="flex items-center">
            <span class="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <strong class="mr-1">Review and update:</strong> Return to complete the missing information
          </div>
          <div class="flex items-center">
            <span class="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
            <strong class="mr-1">Save anyway:</strong> Save the exam with only the complete questions
          </div>
        </div>
      </div>
    </div>

    <template #footer>
        <div class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
        <button
          @click="closeModal"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          Review & Update
        </button>
        <button
          @click="confirmSave"
          class="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 rounded-lg transition-colors"
        >
          <Save class="w-4 h-4 mr-2 inline" />
          Save Anyway ({{ completeCount }} question{{ completeCount !== 1 ? 's' : '' }})
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import { AlertTriangle, Save } from 'lucide-vue-next'

export interface IncompleteQuestion {
  index: number
  question: string
  issues: string[]
  isComplete: boolean
}

interface Props {
  isVisible: boolean
  incompleteQuestions: IncompleteQuestion[]
}

const props = defineProps<Props>()

// Methods to centralize closing and confirming logic
const closeModal = () => {
  // Perform any cleanup here if needed, then emit close
  // Example: focus management or resetting state in parent can be handled there
  emitClose()
}

const confirmSave = () => {
  // Any pre-confirm checks could go here before emitting
  emitConfirm()
}

// Create typed emit functions
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'confirm'): void
}>()

function emitConfirm() {
  emit('confirm')
}

function emitClose() {
  emit('close')
}

// Computed properties
const incompleteCount = computed(() => {
  return props.incompleteQuestions.filter(q => !q.isComplete).length
})

const completeCount = computed(() => {
  return props.incompleteQuestions.filter(q => q.isComplete).length
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
